import { forwardRef, useState } from 'react'
import { DraggableProvidedDragHandleProps } from 'react-beautiful-dnd'
import { useTranslation } from 'react-i18next'
import isEmpty from 'lodash/isEmpty'
import { GETListControlFilter, ListControlFilterForm } from 'src/types'
import { Button } from '@microservices/wiskey-react-components'
import {
  Attachment as AttachmentIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIndicatorIcon,
  SpaceBar as SpaceBarIcon,
  TableView as TableViewIcon,
  TextFields as TextFieldsIcon,
} from '@mui/icons-material'
import { Box, Grid, IconButton, SxProps, Theme, Typography } from '@mui/material'
import { grey } from '@mui/material/colors'

import { getDataFromState } from '@components/CommandParameters/helpers'
import { OuterParametersDialog } from '@components/OuterParametersDialog'

import { AVAILABLE_FORM_ELEMENT_TYPE, FORM_ELEMENT_TYPE } from '../../../../../constants'

type FieldProps = {
  isEditOrPopup: boolean
  isSmallSize: boolean
  dragHandlePropsForIcon: DraggableProvidedDragHandleProps | undefined
  dragHandlePropsForContainer: DraggableProvidedDragHandleProps | undefined
  elementData?: string | number
  elementType: FORM_ELEMENT_TYPE
  elementTitle: string | undefined
  objectCode: string
  textTitle: string
  elementOuterParameters?: GETListControlFilter[]
  elementId: number
  handleDeleteFieldOnForm: () => void
  onSubmitFilter: (filter: GETListControlFilter) => Promise<any>
  onDeleteFilter: (id: number) => void
  isDeleteElementInProgress?: boolean
}

const itemIcon = {
  [AVAILABLE_FORM_ELEMENT_TYPE.VIEW]: <TableViewIcon color={'warning'} />,
  [AVAILABLE_FORM_ELEMENT_TYPE.TEXT]: <TextFieldsIcon color={'primary'} />,
  [AVAILABLE_FORM_ELEMENT_TYPE.SPACE]: <SpaceBarIcon color={'success'} />,
  [AVAILABLE_FORM_ELEMENT_TYPE.ACTION_BUTTON]: <AttachmentIcon color='warning' />,
}

const wrapperStyles: SxProps<Theme> = {
  padding: '16px 16px 16px 24px',
  gap: '16px',
  py: '14px',
  justifyContent: 'space-between',
  userSelect: 'none',
  height: 56,
  mx: 1,
  display: 'flex',
  alignItems: 'center',
  border: '1px solid',
  borderRadius: '4px',
  borderColor: grey[300],
  backgroundColor: theme => theme.palette.background.paper,
}

const Field = forwardRef<HTMLDivElement | null, FieldProps>(
  (
    {
      isEditOrPopup,
      isSmallSize,
      dragHandlePropsForIcon,
      dragHandlePropsForContainer,
      elementData,
      elementType,
      elementTitle,
      objectCode,
      textTitle,
      elementOuterParameters,
      elementId,
      handleDeleteFieldOnForm,
      onSubmitFilter,
      onDeleteFilter,
      isDeleteElementInProgress = false,
    },
    newItemRef
  ) => {
    const { t } = useTranslation()
    const [isShowOuterParametersDialog, setShowOuterParametersDialog] = useState(false)
    const [currentOuterParametersData, setCurrentOuterParametersData] =
      useState<GETListControlFilter | null>(null)

    const handleOpenOuterParameters = () => {
      if (elementOuterParameters && !isEmpty(elementOuterParameters)) {
        setCurrentOuterParametersData(elementOuterParameters[0])
      }

      setShowOuterParametersDialog(true)
    }

    const handleCloseFilterDialog = (isDirty: boolean) => {
      if (isDirty && !confirm(t('notifications.leave'))) {
        return
      }
      setCurrentOuterParametersData(null)
      setShowOuterParametersDialog(false)
    }

    const handleSubmitFilter = (currentOuterParametersData: ListControlFilterForm) => {
      onSubmitFilter({
        ...currentOuterParametersData,
        commands: currentOuterParametersData.commands.map(command => {
          return {
            ...command,
            name: command.name?.id,
            objectCode: command.sourceCommand?.id,
            objectTitle: command.sourceCommand?.label,
            type: command.type?.id,
            resource: command.sourceType?.id,
            parameters: getDataFromState(command.parameters),
            body: getDataFromState(command.body),
          }
        }),
        elementId,
      }).then(({ data }) => {
        if (!currentOuterParametersData.id) {
          setCurrentOuterParametersData(data)
        }
      })

      if (currentOuterParametersData.id) {
        setShowOuterParametersDialog(false)
        setCurrentOuterParametersData(null)
      }
    }

    return (
      <Box
        sx={wrapperStyles}
        {...dragHandlePropsForContainer}
        title={isSmallSize ? textTitle : undefined}
      >
        <Grid container item alignItems={'center'} flexWrap={'nowrap'}>
          <Grid
            ref={newItemRef}
            container
            item
            alignItems={'baseline'}
            flexWrap={'nowrap'}
            width={'auto'}
            {...dragHandlePropsForIcon}
          >
            {!isEditOrPopup && !isSmallSize && (
              <DragIndicatorIcon
                fontSize={'small'}
                sx={{
                  alignSelf: 'center',
                  color: theme => (theme.palette.mode === 'light' ? grey[600] : grey[300]),
                }}
              />
            )}
            {elementType !== FORM_ELEMENT_TYPE.FIELD && (
              <Grid item height={24}>
                {itemIcon[elementType]}
              </Grid>
            )}
          </Grid>
          {!isEditOrPopup && (
            <Grid item flex={1} overflow={'hidden'}>
              <Typography
                title={elementTitle}
                sx={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {elementTitle}
              </Typography>
            </Grid>
          )}
          {isShowOuterParametersDialog && (
            <OuterParametersDialog
              currentOuterParametersData={currentOuterParametersData}
              entityId={elementData as number}
              isShow={isShowOuterParametersDialog}
              objectCode={objectCode}
              onClose={handleCloseFilterDialog}
              onSubmit={handleSubmitFilter}
            />
          )}
          {elementType === FORM_ELEMENT_TYPE.VIEW && (
            <Grid container item alignItems={'center'} flex={4} flexWrap={'inherit'} gap={2} mx={2}>
              <Button sx={{ width: 230 }} variant='outlined' onClick={handleOpenOuterParameters}>
                {t('fieldsConfig.outerParameters.addEditButton', {
                  mode: isEmpty(elementOuterParameters) ? 'add' : 'edit',
                })}
              </Button>
            </Grid>
          )}
          {!isEditOrPopup && !isSmallSize && (
            <IconButton
              disabled={isDeleteElementInProgress}
              size={'small'}
              sx={{ ml: 'auto', p: 0 }}
              onClick={handleDeleteFieldOnForm}
            >
              <DeleteIcon fontSize={'medium'} />
            </IconButton>
          )}
        </Grid>
      </Box>
    )
  }
)

Field.displayName = 'Field'

export { Field }
